import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

export const useAnnouncements = () => {
  const { household } = useAuth()
  const qc = useQueryClient()
  const key = ['announcements', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled: !!household?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('id, title, body, is_pinned, created_at, updated_at, author_id, profiles(display_name, avatar_url, member_color)')
        .eq('household_id', household.id)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  // Realtime subscription
  useEffect(() => {
    if (!household?.id) return
    const channel = supabase
      .channel(`announcements-${household.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'announcements', filter: `household_id=eq.${household.id}` },
        () => qc.invalidateQueries({ queryKey: key }),
      )
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [household?.id, qc])

  return query
}

export const useAnnouncementMutations = () => {
  const { household, user } = useAuth()
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['announcements', household?.id] })

  const create = useMutation({
    mutationFn: async ({ title, body, is_pinned = false }) => {
      const { error } = await supabase.from('announcements').insert({
        household_id: household.id,
        author_id: user.id,
        title,
        body,
        is_pinned,
      })
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({ id, title, body, is_pinned }) => {
      const { error } = await supabase
        .from('announcements')
        .update({ title, body, is_pinned, updated_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('announcements').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const togglePin = useMutation({
    mutationFn: async ({ id, is_pinned }) => {
      const { error } = await supabase.from('announcements').update({ is_pinned }).eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { create, update, remove, togglePin }
}
